import * as React from 'react'
import { FlatList, View } from 'react-native'
import styled from '@theme/styled-components'

import ModalHeader from '@components/filter/ModalHeader'
import TextInputComponent from '@components/textInput/TextInput'
import Checkbox from '@components/checkbox/Checkbox'
import Button from '@components/button/Button'

import useI18n from '@i18n/useI18n'
import sanitize from '@utils/Sanitizer'

interface Props {
  title: string
  onBack: () => void
  items: FilterItem[]
  updateFilter: (filter: { name: string; value: boolean | FilterItem[] }) => void
  filterName: string
  placeholder: string
  initValues: FilterItem[]
}

const ItemSelection = ({ title, onBack, items, filterName, updateFilter, placeholder, initValues }: Props) => {
  const [searchText, setSearchText] = React.useState('')
  const [selectedItems, setSelectedItems] = React.useState<FilterItem[]>(initValues)
  const i18n = useI18n()

  const itemsToDisplay = React.useMemo(() => {
    const sanitized = sanitize(searchText)
    if (!!sanitized) {
      return items.filter((item) => sanitize(item.name).includes(sanitized))
    }
    return items
  }, [searchText])

  const updateSelected = (item: FilterItem) => {
    if (selectedItems.includes(item)) {
      setSelectedItems((prevState) => prevState.filter((selected) => selected.id !== item.id))
    } else {
      setSelectedItems((prevState) => [...prevState, item])
    }
  }

  const renderItem = ({ item }: { item: FilterItem }) => {
    return (
      <CheckboxContainer key={item.id}>
        <Checkbox
          iconOnTheRight
          label={item.name}
          onClick={() => updateSelected(item)}
          value={selectedItems.some((e) => e.id === item.id)}
        />
      </CheckboxContainer>
    )
  }

  const validateSelection = () => {
    const filter = { name: filterName, value: selectedItems }
    updateFilter(filter)
    onBack()
  }

  return (
    <>
      <ModalHeader
        left={{ name: 'arrow-left', onClick: onBack }}
        title={title}
        right={{
          label: i18n.t('screens.incidentList.filters.modal.headers.erase'),
          onClick: () => setSelectedItems([]),
        }}
      />
      <TextInputComponent
        onChange={setSearchText}
        searchStyle="onLight"
        left={{ name: 'search' }}
        placeholder={placeholder}
      />
      <Body>
        <FlatList
          data={itemsToDisplay}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          showsVerticalScrollIndicator={false}
        />
      </Body>
      <Footer>
        <Button label={i18n.t('screens.incidentList.validateFilter')} onClick={validateSelection} status="active" />
      </Footer>
    </>
  )
}

const CheckboxContainer = styled(View)`
  margin-bottom: 15px;
`

const Body = styled(View)`
  margin-top: 25px;
  flex: 1;
`

const Footer = styled(View)`
  padding: 16px;
`

export default ItemSelection
